import { Pipe, PipeTransform } from '@angular/core';
import { ArticleType, Article, ArticleItem } from '../core/article';
import { UserTicketTrip, UserTicket, PaymentStatus, TicketStatus } from '../core/userTicket';

import * as moment from 'moment';
import { TicketOffer } from '../core/ticketOffer';

@Pipe({ name: 'moment' })
export class TicketMomentPipe implements PipeTransform {
    transform(date: Date | string, format: string): string {
        return format === 'now' ? moment(date).fromNow() : moment(date).format(format);
    }
}

@Pipe({ name: 'stopCountText' })
export class StopCountTextPipe implements PipeTransform {
    transform(stopCount: number): string {
        const remainder = stopCount % 10;
        let word: string;

        if (stopCount >= 11 && stopCount <= 19) {
            word = 'остановок';
        } else {
            word = remainder === 0
                ? 'остановок'
                : remainder === 1
                    ? 'остановка'
                    : remainder < 5
                        ? 'остановки'
                        : 'остановок';
        }

        return `${stopCount} ${word}`;
    }
}

@Pipe({ name: 'transferCountText' })
export class TransferCountTextPipe implements PipeTransform {
    transform(transferCount: number): string {
        const remainder = transferCount % 10;
        let word: string;

        if (transferCount >= 11 && transferCount <= 19) {
            word = 'пересадок';
        } else {
            word = remainder === 0
                ? 'пересадок'
                : remainder === 1
                    ? 'пересадка'
                    : remainder < 5
                        ? 'пересадки'
                        : 'пересадок';
        }

        return `${transferCount} ${word}`;
    }
}

@Pipe({
    name: 'filterItems',
    pure: false
})
export class FilterArticleItems implements PipeTransform {
    transform(items: ArticleItem[], filter: ArticleType): ArticleItem[] {
        if (!items || !filter) {
            return items;
        }
        return items.filter(i => i.type === filter);
    }
}


@Pipe({ name: 'activeTrips' })
export class TicketActiveTripsPipe implements PipeTransform {
    transform(trips: UserTicketTrip[]): UserTicketTrip[] {
        return trips
            ? trips.filter(t => t.pass && !t.pass.isUsed && !t.pass.isExpiredNow)
            : [];
    }
}

@Pipe({ name: 'usedTrips' })
export class TicketUsedTripsPipe implements PipeTransform {
    transform(trips: UserTicketTrip[]): UserTicketTrip[] {
        return trips
            ? trips.filter(t => t.pass && (t.pass.isUsed || t.pass.isExpiredNow))
            : [];
    }
}

@Pipe({ name: 'ticketActiveTripsCount' })
export class TicketActiveTripsCountPipe implements PipeTransform {
    private activeTripsPipe: TicketActiveTripsPipe = new TicketActiveTripsPipe();

    transform(ticket: UserTicket): number {
        return this.activeTripsPipe.transform(ticket.trips).length + ticket.remainingTripsCount;
    }
}

@Pipe({ name: 'ticketIsActive' })
export class TicketIsActivePipe implements PipeTransform {
    private activeTripsCountPipe: TicketActiveTripsCountPipe = new TicketActiveTripsCountPipe();

    transform(ticket: UserTicket): boolean {
        switch (ticket.status) {
            case TicketStatus.inactive:
                return false;
            case TicketStatus.active:
            case TicketStatus.used:
                const count = this.activeTripsCountPipe.transform(ticket);
                return count > 0;
        }
    }
}


@Pipe({ name: 'ticketStatusDisplay' })
export class TicketStatusDisplayPipe implements PipeTransform {
    private activeTripsPipe: TicketActiveTripsPipe = new TicketActiveTripsPipe();
    transform(ticket: UserTicket): string {
        switch (ticket.status) {
            case TicketStatus.inactive:
                return 'Не оплачен';
            case TicketStatus.active:
            case TicketStatus.used:
                const activeCount = this.activeTripsPipe.transform(ticket.trips).length;
                return activeCount > 0 || ticket.remainingTripsCount > 0 ? 'Активен' : 'Использован';
        }
    }
}

@Pipe({ name: 'paymentStatusDisplay' })
export class TicketPaymentStatusDisplayPipe implements PipeTransform {
    transform(paymentStatus: PaymentStatus): string {
        switch (paymentStatus) {
            case PaymentStatus.pending:
                return 'Не оплачен';
            case PaymentStatus.successful:
                return 'Оплачен';
            case PaymentStatus.failed:
                return 'Оплата не была завершена успешно';
        }
    }
}

@Pipe({ name: 'tripCountText' })
export class TripCountTextPipe implements PipeTransform {
    transform(tripCount: number): string {
        const remainder = tripCount % 10;
        let word: string;

        if (tripCount >= 11 && tripCount <= 19) {
            word = 'поездок';
        } else {
            word = remainder === 0
                ? 'поездок'
                : remainder === 1
                    ? 'поездку'
                    : remainder < 5
                        ? 'поездки'
                        : 'поездок';
        }

        return `${tripCount} ${word}`;
    }
}

@Pipe({ name: 'shortTripCountText' })
export class ShortTripCountTextPipe implements PipeTransform {
    transform(ticketOfferCount: number): string {
        return ticketOfferCount === 1
            ? 'поездка'
            : ticketOfferCount > 1 && ticketOfferCount < 5
                ? 'поездки'
                : 'поездок';
    }
}

@Pipe({ name: 'ticketBonusOffers' })
export class TicketBonusOffersPipe implements PipeTransform {
    transform(ticketOffers: TicketOffer[]) {
        return ticketOffers ? ticketOffers.filter(t => t.bonusTripsCount !== 0 || t.discount !== 0) : [];
    }
}

@Pipe({ name: 'ticketBonusOffersText' })
export class TicketBonusOffersTextPipe implements PipeTransform {
    transform(offer: TicketOffer): string {
        const bonus = offer.bonusTripsCount;

        return [
            bonus === 1 ? 'поездка' : bonus < 5 ? 'поездки' : 'поездок',
            `в подарок`
        ].join(' '); 
    }
}

@Pipe({ name: 'ticketOffersText' })
export class TicketOffersTextPipe implements PipeTransform {
    transform(offer: TicketOffer): string {
        const min = offer.minTripsCount;
        const max = offer.maxTripsCount;

        return [
            `при покупке от ${min}`,
            max ? `до ${max}` : '',
            max === 1 ? 'поездки' : 'поездок'
        ].join(' ');
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/pipe-index.ts